// src/services/pdf/generatePresupuesto.js
// Generates a branded "Presupuesto" (budget/quote) PDF for an event.

import { jsPDF } from "jspdf";
import {
    preloadLogos,
    drawMinimalistHeader,
    drawSnapshot,
    drawServiceBlock,
    drawExclusionsBox,
    drawExecutiveSummary,
    checkPageBreak,
    drawFooter,
} from "./pdfBranding.js";
import {
    COMPANY,
    COLORS,
    IVA_RATE,
    formatCurrency,
    formatDate,
    todayFormatted,
    generateDocNumber,
    safeFileName,
} from "./pdfConfig.js";

/**
 * Generate a Presupuesto (Quote/Budget) PDF.
 * @param {object} event       - Datos del evento
 * @param {object} contact     - Contacto principal { name, email, phone, nif }
 * @param {object} companyData - Datos de la empresa (CIF, IBAN, etc.)
 * @param {string} docNumber   - Número de documento ya generado/incrementado
 */
export async function generatePresupuestoPDF(event, contact = {}, companyData = {}, docNumber = "") {

    if (!event) {
        console.error("No event data for Presupuesto");
        return;
    }

    await preloadLogos();
    const doc = new jsPDF({ compress: true });
    const finalDocNumber = docNumber || generateDocNumber("PRES"); 
    const pageWidth = doc.internal.pageSize.getWidth();


    // === A. CABECERA (Branding y Emisor) ===
    let y = drawMinimalistHeader(doc, { docNumber: finalDocNumber, company: companyData });


    // === B. EL "SNAPSHOT" DEL EVENTO (Bloque de Identificación) ===
    y = drawSnapshot(doc, y, {
        client: [
            contact.name || event.client_name_legacy || "—",
            `DNI/CIF: ${contact.nif || "________________"}`,
            contact.fiscal_address || "",
            `${contact.fiscal_zip || ""} ${contact.fiscal_city || ""}`.trim() || contact.email || "—"
        ],

        details: [
            `Nº Presupuesto: ${finalDocNumber}`,
            `Fecha emisión: ${todayFormatted()}`,
            "Validez: 30 días naturales"
        ],
        event: [
            `Fecha: ${formatDate(event.date)}`,
            `Ubicación: ${event.city || event.location || event.region || "A determinar"}`,
            `Tipo: ${capitalize(event.type || "Evento")}`
        ]
    });

    y += 10;

    // === C. PROPUESTA TÉCNICA (Servicios) ===
    // 1. Core Services
    y = drawServiceBlock(doc, y, "Lo que nos hace únicos (Core)", [
        "Actuación musical en vivo (formación estándar Los Toldos)",
        "Repertorio personalizado y curado para el evento",
        "Puesta en escena ejecutiva y profesional"
    ]);

    y += 5;

    // 2. Technical Infrastructure
    y = drawServiceBlock(doc, y, "Infraestructura (Technical)", [
        "Backline completo instrumentos y amplificación propia",
        "Sistema de monitoraje in-ear (IEM) profesional",
        "Gestión logística y coordinación técnica técnica"
    ]);

    y += 10;

    const isOutsideVizcaya = !isVizcaya(event);
    
    // 3. Exclusions Box
    const exclusions = [
        "Escenario con dimensiones mínimas y acometida eléctrica.",
        "Dietas y parking para el equipo (según ubicación)."
    ];
    if (isOutsideVizcaya) {
        exclusions.push("Equipo de sonido (P.A.), técnico de sonido e iluminación profesional.");
        exclusions.push("Estos servicios deben ser aportados por el cliente o contratados aparte.");
    } else {
        exclusions.push("Iluminación decorativa avanzada (disponible como extra).");
    }

    y = drawExclusionsBox(doc, y, exclusions);

    y += 15;

    // === D. RESUMEN ECONÓMICO (La "Golden Box") ===
    const price = Number(event.amount || 0);
    const cuotaIva = price * IVA_RATE;
    const total = price + cuotaIva;

    y = drawExecutiveSummary(doc, y, {
        subtotal: formatCurrency(price),
        iva: formatCurrency(cuotaIva),
        total: formatCurrency(total)
    });

    // === E. "NEXT STEPS" Y CIERRE (Call to Action) ===
    y = checkPageBreak(doc, y, 40);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.TEXT);
    doc.text("Siguientes Pasos", 15, y);

    y += 6;
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    const nextSteps = [
        "1. Confirmación: Responder a este correo confirmando la aceptación.",
        `2. Reserva: Realizar el ingreso de la señal (500€) al IBAN ${companyData.iban || COMPANY.iban}.`,
        "3. Producción: Agendaremos una breve llamada para cerrar detalles de repertorio y logística."
    ];
    nextSteps.forEach(step => {
        doc.text(step, 15, y);
        y += 5;
    });


    y += 10;
    doc.setFontSize(10);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(...COLORS.PRIMARY);
    doc.text("Estamos deseando formar parte de vuestra historia.", pageWidth / 2, y, { align: "center" });

    // === FOOTER ===
    drawFooter(doc);

    // === SAVE ===
    const safeName = safeFileName(event.client_name_legacy);
    doc.save(`Presupuesto_${safeName}_${finalDocNumber}.pdf`);
}


// Helper
function capitalize(str) {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
}

function isVizcaya(ev) {
    const loc = (ev.city || ev.location || ev.region || "").toLowerCase();
    return (
        loc.includes("vizcaya") ||
        loc.includes("bizkaia") ||
        loc.includes("bilbao") ||
        loc.includes("getxo")
    );
}
